import {toList, toString} from "../helpers/helpers";
import {KeyByObject} from "../misc/reduce-helpers";

const readableTypeMap = {
  accessory: "Accessory",
  bundled: "Accessory",
  asset: "Printer",
};

export interface SandyPreImportDataModel {
  assets: number;
  standaloneAccessories: number;
  bundledAccessories: number;
  canImport: boolean;
  isMigrationActive: boolean;
}

export function sandyPreImportDataModelFactory(json?: any) {
  json = json || {};

  return {
    assets: +json.assets,
    standaloneAccessories: +json.standaloneAccessories,
    bundledAccessories: +json.bundledAccessories,
    canImport: !!(json.assets || json.standaloneAccessories || json.bundledAccessories),
    isMigrationActive: json.isMigrationActive || false,
  };
}

export class SandyLineItemModel {
  productNumber: string;
  serialNumber: string;
  orderNumber: string;
  orderDate: string;
  shippingDate: string;
  trackingNumber: string;
  macAddress: string;
  id: string;
  hash: string;
  selected: boolean = false;

  static converter(json?: any): SandyLineItemModel {
    if (json instanceof SandyLineItemModel) return json;
    return new SandyLineItemModel(json);
  }

  constructor(json?: any) {
    json = json || {};

    this.productNumber = toString(json.productNumber);
    this.serialNumber = toString(json.serialNumber);
    this.orderNumber = toString(json.orderNumber);
    this.orderDate = toString(json.orderDate);
    this.shippingDate = toString(json.shippingDate);
    this.trackingNumber = toString(json.trackingNumber);
    this.macAddress = toString(json.macAddress);
    this.id = toString(json.id) || null;
    this.hash = toString(json.hash);
  }
}

export class SandyAssetModel {
  id: string | null;
  assetId: string | null;
  productNumber: string;
  productDescription: string;
  alternateProductNumber: string;
  deviceKey: string;
  orderNumber: string;
  orderDate: string;
  site: string;
  building: string;
  floor: string;
  mapId: string;
  type: "asset" | "accessory" | "bundled" | string;
  readableType: string = "";
  eligibleIds: string[];
  eligibleSandyItems: SandyLineItemModel[] = [];
  sandyId: string | null;
  serialNumber: string = "";
  hash: string;

  static converter(json?: any): SandyAssetModel {
    if (json instanceof SandyAssetModel) return json;
    return new SandyAssetModel(json);
  }

  constructor(json?: any) {
    json = json || {};

    this.id = toString(json.id) || null;
    this.assetId = toString(json.assetId) || null;
    this.productNumber = toString(json.productNumber);
    this.productDescription = toString(json.productDescription);
    this.alternateProductNumber = toString(json.alternateProductNumber);
    this.deviceKey = toString(json.deviceKey);
    this.orderNumber = toString(json.orderNumber);
    this.orderDate = toString(json.orderDate);
    this.site = toString(json.site);
    this.building = toString(json.building);
    this.floor = toString(json.floor);
    this.mapId = toString(json.mapId);
    this.type = toString(json.type);
    this.eligibleIds = toList(toString, json.eligibleIds);
    this.sandyId = toString(json.sandyId) || null;
    this.hash = toString(json.hash);
    this.readableType = readableTypeMap[this.type] || "";
  }

  clearSandyItem() {
    for (const item of this.eligibleSandyItems) {
      if (item.id === this.sandyId) {
        item.selected = false;
        this.sandyId = undefined;
        this.serialNumber = "";
      }
    }
  }

  assignSandyItem(item: SandyLineItemModel) {
    if (this.sandyId) {
      this.clearSandyItem();
    }

    if (item.selected) return;

    item.selected = true;
    this.sandyId = item.id;
    this.serialNumber = item.serialNumber;
  }

  autoAssignSandyItem() {
    if (this.sandyId) return;

    for (const item of this.eligibleSandyItems) {
      if (!item.selected) {
        this.assignSandyItem(item);
        return;
      }
    }
  }
}

export class SandyImportDataModel {
  private message: string = "Done.";

  assets: SandyAssetModel[];
  sandyItems: SandyLineItemModel[];

  static async process(json?: any): Promise<any> {
    return new SandyImportDataModel(json);
  }

  static merge(models: SandyImportDataModel[]): SandyImportDataModel {
    const seenHashes = new Set();
    const result = new SandyImportDataModel();

    for (const model of models) {
      for (const item of model.assets) {
        if (!seenHashes.has(item.hash)) {
          seenHashes.add(item.hash);
          result.assets.push(item);
        }
      }

      for (const item of model.sandyItems) {
        if (!seenHashes.has(item.hash)) {
          seenHashes.add(item.hash);
          result.sandyItems.push(item);
        }
      }
    }

    return result;
  }

  constructor(json?: any) {
    json = json || {};
    if (typeof json === "string") json = JSON.parse(json);

    this.assets = toList(SandyAssetModel.converter, json.assets);
    this.sandyItems = toList(SandyLineItemModel.converter, json.sandyItems);

    const sandyItemIndex = this.sandyItems.reduce((obj, item) => obj.add(item.id, item), new KeyByObject()).toObject();

    for (const item of this.assets) {
      item.eligibleSandyItems = item.eligibleIds.map(id => sandyItemIndex[id])
        .filter(it => it && it.serialNumber);
    }

    if (json.message) this.message = json.message;
  }

  toString() {
    return this.message;
  }
}
